import React, {useContext, useEffect, useRef, useState} from 'react';
import {Dialog, DialogActions, DialogContent, IconButton} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/CloseRounded";
import './style.scss';
import onScan from "onscan.js";
import Order from "../OrdersGrid/Order/Order";
import NavigateNextIcon from '@material-ui/icons/NavigateBefore';
import Button from "@material-ui/core/Button";
import {AppContext} from "../../Utils/AppContext";

onScan.attachTo(document);

export default function BarcodePasteReaderListenerDialog({acceptedOrders, editOrder, logInfo}) {
    const appContext = useContext(AppContext);
    const [openState, setOpenState] = useState(false);
    const [showNextStatusTimer, setShowNextStatusTimer] = useState(false);
    const [chosenOrders, setChosenOrders] = useState([]);
    const chosenOrdersRef = useRef([]);
    const countDownTimeoutRef = useRef();
    const clearChosenOrdersRef = useRef();
    const dialogOpenTimeRef = useRef(null);

    // If countdownMS is changed, change it also in the CSS in next-status-timer-run class.
    const countdownMS = 7000;

    useEffect(() => {
        if (openState)
            dialogOpenTimeRef.current = new Date();

        if (openState && clearChosenOrdersRef.current) {
            setChosenOrders([]);
            chosenOrdersRef.current = [];
            clearChosenOrdersRef.current = null;
        } else if (!openState && !clearChosenOrdersRef.current) {
            clearChosenOrdersRef.current = setTimeout(() => {
                setChosenOrders([]);
                chosenOrdersRef.current = [];
                clearChosenOrdersRef.current = null;
            }, 200);
        }
    }, [openState]);

    function addOrder(order) {
        if (chosenOrdersRef.current.find(o => o.id === order.id))
            return;

        const newChosenOrders = [...chosenOrdersRef.current, order];

        chosenOrdersRef.current = newChosenOrders;
        setChosenOrders(newChosenOrders);

        startCountdownNextStatus();
    }

    function startCountdownNextStatus() {
        setShowNextStatusTimer(false);

        if (countDownTimeoutRef.current)
            clearTimeout(countDownTimeoutRef.current)

        countDownTimeoutRef.current = setTimeout(handleMakeReady, countdownMS);

        setShowNextStatusTimer(true);
    }

    function handlePaste(evt) {
        const text = (event.clipboardData || window.clipboardData).getData('text');

        const trimmedText = text.trim();

        const isOrderAdded = tryAddOrder(trimmedText);

        if (isOrderAdded && !openState)
            setOpenState(true);
    }

    function handleScan(evt) {
        const code = evt.detail.scanCode.trim();

        const isOrderAdded = tryAddOrder(code);

        if (isOrderAdded && !openState)
            setOpenState(true);
    }

    function tryAddOrder(orderId) {
        const foundOrder = acceptedOrders.find(order => order.label === orderId);

        if (foundOrder) {
            addOrder(foundOrder);

            return true;
        }

        return false;
    }

    useEffect(() => {
        document.addEventListener('scan', handleScan);
        document.addEventListener('paste', handlePaste);

        return () => {
            document.removeEventListener('scan', handleScan);
            document.removeEventListener('paste', handlePaste);
        };
    }, [acceptedOrders, chosenOrders])

    function handleCloseDialog() {
        logInfo("closeBarcodePasteDialog", appContext.bid, {
            isStatusChanged: false,
            numberOfOrders: chosenOrdersRef.current.length,
            orderIds: chosenOrdersRef.current.map(order => order.id),
            duration: (new Date() - dialogOpenTimeRef.current) / 1000
        });

        closeDialog();
    }

    function closeDialog() {
        if (countDownTimeoutRef.current) {
            clearTimeout(countDownTimeoutRef.current);
            countDownTimeoutRef.current = null;
        }

        setOpenState(false);
    }

    async function changeOrderStatus(order, newStatus) {
        const newOrder = {...order, status: newStatus};

        if (newStatus !== "SCHEDULED_READY" && "minutesTillReady" in newOrder)
            delete newOrder["minutesTillReady"];

        editOrder(newOrder);
    }

    async function handleMakeReady() {
        for (let order of chosenOrdersRef.current) {
            await changeOrderStatus(order, "READY");
        }

        logInfo("closeBarcodePasteDialog", appContext.bid, {
            isStatusChanged: true,
            numberOfOrders: chosenOrdersRef.current.length,
            orderIds: chosenOrdersRef.current.map(order => order.id),
            duration: (new Date() - dialogOpenTimeRef.current) / 1000
        });

        closeDialog();
    }

    return (
        <Dialog className="barcode-reader-dialog"
                open={openState}
                onClose={handleCloseDialog}
                PaperProps={{className: "barcode-reader-dialog-paper"}}>
            <DialogContent dividers={true}
                           className="dialog-content">
                {
                    chosenOrders && chosenOrders.length > 0 ?
                        <div className="chosen-orders">
                            {
                                chosenOrders.map(order => (
                                    <div key={`choose-order-${order.id}`}
                                         className={"chosen-order"}>
                                        <Order id={`order-${order.id}`}
                                               order={order}
                                               allowEdit={false}/>
                                    </div>
                                ))
                            }
                        </div>
                        :
                        <div className="no-orders-content">
                            <div className="scan-barcode-text">
                                סרוק ברקודים כדי להוסיף הזמנות
                            </div>
                        </div>
                }
            </DialogContent>
            <DialogActions className="dialog-actions">
                <IconButton className="close-button"
                            onClick={handleCloseDialog}>
                    <CloseIcon fontSize="small"/>
                </IconButton>
                <Button variant="contained" onClick={handleCloseDialog}>
                    בטל
                </Button>
                <Button variant="contained" color="primary" onClick={handleMakeReady}
                        disabled={!chosenOrders || chosenOrders.length === 0}
                        className="next-status-button">
                    {!chosenOrders || chosenOrders.length !== 1 ?
                        "הפוך הזמנות למוכנות" :
                        "הפוך הזמנה למוכנה"
                    }
                    <span
                        className={"next-status-icon" + (!chosenOrders || chosenOrders.length === 0 ? " next-status-icon-disabled" : "")}>
                        <NavigateNextIcon/>
                    </span>
                    {
                        showNextStatusTimer ?
                            <span className="next-status-timer next-status-timer-run"/>
                            :
                            null
                    }
                </Button>
            </DialogActions>
        </Dialog>
    );
}